import { useId, useMemo } from "react"

import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"

import { Status, useInsightPanelDataFetcher } from "./useInsightPanelDataFetcher"
import {
  InsightsPanel,
  InsightsPanelContent,
  InsightsPanelErrorDefault,
  InsightsPanelHeader,
} from "~/src/components/Dashboard/InsightsPanel"
import { WithErrors } from "~/src/types/withErrors"

export type Granularity = "hour" | "day" | "month" | "year"

function formatBucketLabel(startISO: string, _endISO: string, granularity: Granularity) {
  const start = new Date(startISO)

  switch (granularity) {
    case "hour": {
      const startLabel = start.toLocaleTimeString(undefined, { hour: "numeric", hour12: true })
      const endLabel = new Date(start.getTime() + 60 * 60 * 1000) // add 1h for end of bucket display
        .toLocaleTimeString(undefined, { hour: "numeric", hour12: true })
      return `${startLabel} - ${endLabel}`
    }
    case "day":
      return start.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })

    case "month":
      return start.toLocaleDateString(undefined, { month: "short", year: "numeric" })

    case "year":
      return String(start.getFullYear())
  }
}

function toChartData(data?: OrderVolumeData) {
  if (!data) return []
  const { granularity } = data
  return data.points.map((p) => ({
    date: formatBucketLabel(p.startTime, p.endTime, granularity),
    startTime: p.startTime,
    endTime: p.endTime,
    orders: p.count,
  }))
}

export type StorefrontInsightsOrderVolumeRenderPanelProps = {
  status: Status
  ordersData: OrderVolumeData | undefined
}

export type OrderVolumeData = WithErrors<{
  granularity: "hour" | "day" | "month" | "year"
  points: Array<{
    startTime: string // start of bucket
    endTime: string // end of bucket
    count: number
  }>
  summary: { total: number }
}>

export function StorefrontInsightsOrderVolumePanel() {
  const { isLoading, data } = useInsightPanelDataFetcher<OrderVolumeData>("order_volume")

  let status: Status
  if (isLoading) status = "loading"
  else if (data?.errors) status = "error"
  else if (data?.summary?.total && data?.points?.length) status = "success"
  else status = "noData"

  return <StorefrontInsightsOrderVolumeRenderPanel status={status} ordersData={data} />
}

export function StorefrontInsightsOrderVolumeRenderPanel({
  status,
  ordersData,
}: StorefrontInsightsOrderVolumeRenderPanelProps) {
  return (
    <InsightsPanel className="@container h-full w-full">
      <InsightsPanelHeader title="Order volume" />
      <InsightsPanelContent className="overflow-y-auto px-4 py-6">
        <div className="h-full">
          <OrderVolumeContent status={status} data={ordersData} />
        </div>
      </InsightsPanelContent>
    </InsightsPanel>
  )
}

function OrderVolumeContent({ status, data }: { status: Status; data: OrderVolumeData | undefined }) {
  if (status === "loading") return <OrderVolumeSkeleton />
  if (status === "error") return <InsightsPanelErrorDefault />
  if (status === "noData") return <OrderVolumeNoData />
  return <OrderVolumeChart data={data} />
}

function OrderVolumeChart({ data }: { data: OrderVolumeData | undefined }) {
  const gradientId = useId()
  const chartData = useMemo(() => toChartData(data), [data])

  return (
    <div className="flex h-full flex-col gap-2">
      <div className="shrink-0">
        <p className="text-2xl font-medium text-gray-900">{data?.summary.total.toLocaleString()}</p>
        <p className="text-gray-500">Orders placed</p>
      </div>

      <div className="flex-1 overflow-hidden">
        <ResponsiveContainer height="100%" width="100%" minHeight={200}>
          <AreaChart data={chartData} className="**:outline-none">
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#2b5dd1" stopOpacity={0.4} />
                <stop offset="75%" stopColor="#2b5dd1" stopOpacity={0} />
              </linearGradient>
            </defs>

            <XAxis dataKey="date" hide />
            <YAxis type="number" hide domain={["dataMin", "dataMax"]} />
            <Tooltip wrapperStyle={{ outline: "none" }} content={<OrderVolumeTooltip />} />

            <Area
              type="monotone"
              dataKey="orders"
              stroke="#2b5dd1"
              strokeWidth={5}
              fill={`url(#${gradientId})`}
              dot={false}
              activeDot={{ r: 4 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

function OrderVolumeTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: Array<{ payload: { date: string; orders: number } }>
}) {
  if (!active || !payload?.length) return null
  const { date, orders } = payload[0].payload

  return (
    <div className="text-md rounded-xl bg-white px-3 py-2 text-gray-900 shadow-md ring-1 ring-black/5">
      <p>
        Orders:
        <span className="ml-2 text-base font-semibold">{orders.toLocaleString()}</span>
      </p>
      <p className="text-gray-500">{date}</p>
    </div>
  )
}

function OrderVolumeNoData() {
  return (
    <div className="flex h-full flex-col gap-4">
      <div className="shrink-0">
        <p className="text-2xl font-medium text-gray-900">0</p>
        <p className="text-gray-500">Orders placed</p>
      </div>
      <div className="flex-1 overflow-hidden">
        <ResponsiveContainer height="100%" width="100%" minHeight={200}>
          <AreaChart
            data={[
              { date: "", orders: 0 },
              { date: "", orders: 0 },
            ]}
            className="**:outline-none"
          >
            <Area
              type="monotone"
              dataKey="orders"
              stroke="#2b5dd1"
              strokeWidth={5}
              fill="url(#ordersAreaColor)"
              activeDot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

function OrderVolumeSkeleton() {
  const skeletonData = [
    { orders: 32 },
    { orders: 45 },
    { orders: 38 },
    { orders: 54 },
    { orders: 41 },
    { orders: 59 },
    { orders: 36 },
  ]
  return (
    <div className="flex h-full animate-pulse flex-col gap-4">
      <div className="h-3 w-12 rounded-full bg-zinc-200" />
      <div className="h-3 w-24 rounded-full bg-zinc-200" />
      <div className="flex-1 overflow-hidden">
        <ResponsiveContainer height="100%" width="100%" minHeight={200}>
          <AreaChart data={skeletonData} className="**:outline-none">
            <YAxis type="number" hide={true} domain={["dataMin", "dataMax"]} />
            <Area type="monotone" dataKey="orders" stroke="#e5e7eb" strokeWidth={5} fill="white" activeDot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
